import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../style/Portfolios.module.css';
import NewPortfolioModal from '../modal/NewPortfolioModal.js';
import { useAuth } from 'react-oidc-context';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPromptToast from '../../../partials/LoginPromptToast/LoginPromptToast.js';
import { useTranslation } from 'react-i18next';

// 로직 분리: 커스텀 훅 및 서브 컴포넌트 import
import { usePortfoliosData } from '../hooks/usePortfoliosData.js';
import { usePeriodSelectors } from '../hooks/usePeriodSelectors.js';
import PortfolioTableBody from './Rank-LiveRank_TableBody.js';

// const PERIODS_KEYS = ['1m', '3m', '6m', '1y', 'all'];
const PERIODS_KEYS = ['1w', '2w', '1m', '3m', '6m', '1y', 'all'];

// =======================================================================
// ⭐ 추가: 초기 로딩을 위한 스켈레톤 컴포넌트
// =======================================================================
const SkeletonRow = ({ styles, columns = 11 }) => (
  <tr className={styles.skeletonRow}>
    {Array.from({ length: columns }).map((_, colIndex) => (
      <td key={colIndex}>
        <div className={styles.skeletonItem}></div>
      </td>
    ))}
  </tr>
);

// 💡 툴팁용 정보 아이콘 컴포넌트 추가 (PortfolioDetailRadarChart.js와 유사하게)
const InfoIcon = () => (
  <svg 
    width="14" height="14" viewBox="0 0 24 24" 
    fill="none" stroke="currentColor" strokeWidth="2.5" 
    strokeLinecap="round" strokeLinejoin="round"
    style={{ marginLeft: '4px', cursor: 'pointer', verticalAlign: 'middle', color: '#888' }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const PortfolioSkeletonBody = ({ styles, rowCount = 8 }) => (
  <tbody>
    {Array.from({ length: rowCount }).map((_, index) => (
      <SkeletonRow key={index} styles={styles} />
    ))}
  </tbody>
);
// =======================================================================
// ⭐ 추가: 초기 로딩을 위한 스켈레톤 컴포넌트 끝
// =======================================================================


export default function Portfolios() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 1. 데이터 및 무한 스크롤 로직을 훅에서 가져오기
  const {
    items, initialLoading, tableLoading, isFetchingMore, hasMore,
    sortConfig, requestSort, observerTargetRef, tableWrapperRef
  } = usePortfoliosData('1w');

  // 2. 기간 선택 로직을 훅에서 가져오기
  // 💡 sortConfig를 usePeriodSelectors에 추가로 전달합니다.
  const {
      PERIODS_LABELS, LIMITED_LABELS, returnIndex, ddIndex, sharpeIndex, calmarIndex,
      sortinoIndex, volIndex, getPeriodKey,
      handleReturnChange, handleDdChange, handleSharpeChange,
      handleCalmarChange, handleSortinoChange, handleVolChange
  } = usePeriodSelectors(t, PERIODS_KEYS, requestSort, sortConfig);

  // 3. 값 계산 함수 (유틸리티로 분리할 수도 있지만, 여기서는 간결화를 위해 유지)
  const getReturnValue = (item) => (item[`ret_${getPeriodKey(returnIndex)}`] ?? 0) * 100;
  const getDdValue = (item) => (item[`mdd_${getPeriodKey(ddIndex)}`] ?? 0) * 100;
  const getSharpeValue = (item) => item[`sharpe_${getPeriodKey(sharpeIndex)}`] ?? 0;
  const getCalmarValue = (item) => item[`calmar_${getPeriodKey(calmarIndex)}`] ?? 0;
  const getSortinoValue = (item) => item[`sortino_${getPeriodKey(sortinoIndex)}`] ?? 0;
  const getVolValue = (item) => (item[`vol_${getPeriodKey(volIndex)}`] ?? 0) * 100;

  const handleLockedMetricClick = () => {
    toast.info(t('portfolios.lockedTooltip'), {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
  };

  // =======================================================================
  // ⭐ 변경: initialLoading에 따른 조건부 렌더링 적용
  // =======================================================================
  const isListEmpty = items.length === 0 && !tableLoading && !isFetchingMore && !initialLoading;

  return (
    <div>
      {isListEmpty ? ( // initialLoading이 false일 때만 빈 목록 표시
        <p className={styles.portfolioEmpty}>{t('portfolios.emptyList')}</p>
      ) : (
        <div ref={tableWrapperRef} className={styles.portfolioTableWrapper}>
          <table className={styles.portfolioTable}>
            <thead>
              {/* 1st Row: 정렬 기준 헤더 */}
              <tr>
                <th className={styles.rankHeader}></th>
                
                {/* User Header */}
                <th
                  onClick={() => requestSort('nickname')}
                  className={`${styles.userHeader} ${sortConfig.key === 'nickname' ? styles.activeHeader : ''} ${sortConfig.key === 'nickname' && sortConfig.direction === 'desc' ? styles.desc : ''}`}
                >
                  {t('portfolios.tableHeaders.user')}
                </th>

                {/* Portfolio Name Header */}
                <th className={`${styles.nameHeader}`}>
                    {t('portfolios.tableHeaders.portfolio')}
                </th>

                {/* Return Header */}
                <th
                  onClick={() => requestSort(`ret_${getPeriodKey(returnIndex)}`)}
                  className={`${sortConfig.key.startsWith('ret_') ? styles.activeHeader : ''} ${sortConfig.key.startsWith('ret_') && sortConfig.direction === 'desc' ? styles.desc : ''}`}
                >
                  {t('portfolios.tableHeaders.return')} 
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.return', '선택한 기간 동안의 수익률입니다.')}</span>
                  </span>
                </th>

                {/* MDD Header */}
                <th
                  onClick={() => {
                    // 1w, 2w가 아닐 때만 정렬 요청
                    const currentPeriod = getPeriodKey(ddIndex);
                    if (currentPeriod !== '1w' && currentPeriod !== '2w') {
                      requestSort(`mdd_${currentPeriod}`);
                    }
                  }}
                  className={`
                    ${(sortConfig.key.startsWith('mdd_') && !['1w', '2w'].includes(getPeriodKey(ddIndex))) ? styles.activeHeader : ''} 
                    ${(sortConfig.key.startsWith('mdd_') && sortConfig.direction === 'desc') ? styles.desc : ''}
                    ${(['1w', '2w'].includes(getPeriodKey(ddIndex))) ? styles.disabledHeader : ''}
                  `}
                >
                  {t('portfolios.tableHeaders.maxDrawdown')} 
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.maxDrawdown', '최고점 대비 최대 하락폭입니다.')}</span>
                  </span>
                </th>

                {/* Volatility Header */}
                <th
                  onClick={() => {
                    // 1w, 2w가 아닐 때만 정렬 요청
                    const currentPeriod = getPeriodKey(volIndex);
                    if (currentPeriod !== '1w' && currentPeriod !== '2w') {
                      requestSort(`vol_${currentPeriod}`);
                    }
                  }}
                  className={`
                    ${(sortConfig.key.startsWith('vol_') && !['1w', '2w'].includes(getPeriodKey(volIndex))) ? styles.activeHeader : ''} 
                    ${(sortConfig.key.startsWith('vol_') && sortConfig.direction === 'desc') ? styles.desc : ''}
                    ${(['1w', '2w'].includes(getPeriodKey(volIndex))) ? styles.disabledHeader : ''}
                  `}
                >
                  {t('portfolios.tableHeaders.volatility')}
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.volatility', '가격 변동의 표준편차입니다.')}</span>
                  </span>
                </th>

              
                
                {/* Sharpe Header */}
                <th
                  onClick={() => {
                    // 1w, 2w가 아닐 때만 정렬 요청
                    const currentPeriod = getPeriodKey(sharpeIndex);
                    if (currentPeriod !== '1w' && currentPeriod !== '2w') {
                      requestSort(`sharpe_${currentPeriod}`);
                    }
                  }}
                  className={`
                    ${(sortConfig.key.startsWith('sharpe_') && !['1w', '2w'].includes(getPeriodKey(sharpeIndex))) ? styles.activeHeader : ''} 
                    ${(sortConfig.key.startsWith('sharpe_') && sortConfig.direction === 'desc') ? styles.desc : ''}
                    ${(['1w', '2w'].includes(getPeriodKey(sharpeIndex))) ? styles.disabledHeader : ''}
                  `}
                >
                  {t('portfolios.tableHeaders.sharpe')}
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.sharpe', '위험 대비 수익률(효율성) 지표입니다.')}</span>
                  </span>
                </th>
                
                {/* Sortino Header */}
                <th>
                  {t('portfolios.tableHeaders.sortino')}
                  {/* ⭐ 변경: 툴팁 컨테이너와 InfoIcon 추가 */}
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.sortino', '하락 위험만 고려한 위험 대비 수익률입니다.')}</span>
                  </span>
                </th>

                {/* Calmar Header */}
                <th>
                  {t('portfolios.tableHeaders.calmar')}
                  {/* ⭐ 변경: 툴팁 컨테이너와 InfoIcon 추가 */}
                  <span className={styles.infoIconTooltipContainer}>
                    <InfoIcon />
                    <span className={styles.customTooltip}>{t('portfolios.tooltip.calmar', '연평균 수익률을 최대 낙폭으로 나눈 값입니다.')}</span>
                  </span>
                </th>

                {/* Days Header */}
                <th>
                  {t('portfolios.tableHeaders.days')}
                </th>

                <th></th>
              </tr>
              
              {/* 2nd Row: 기간 선택 셀렉터 */}
              <tr>
                <th className={styles.rank2ndHeader}></th>
                <th className={styles.user2ndHeader}></th>
                <th className={`${styles.fixedPeriod} ${styles.name2ndHeader}`}></th>
                {/* 수익률 선택 */}
                <th><select value={PERIODS_LABELS[returnIndex]} onChange={handleReturnChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                {/* MDD 선택 */}
                <th><select value={PERIODS_LABELS[ddIndex]} onChange={handleDdChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                {/* 변동성 선택 */}
                <th><select value={PERIODS_LABELS[volIndex]} onChange={handleVolChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                {/* 샤프 선택 */}
                <th><select value={PERIODS_LABELS[sharpeIndex]} onChange={handleSharpeChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                {/* 소르티노 선택 */}
                <th><select value={PERIODS_LABELS[sortinoIndex]} onChange={handleSortinoChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                {/* 칼마 선택 */}
                <th><select value={PERIODS_LABELS[calmarIndex]} onChange={handleCalmarChange} className={styles.selector}>{PERIODS_LABELS.map((p,i)=><option key={i}>{p}</option>)}</select></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            {/* 5. <tbody> 렌더링: 초기 로딩 중이면 스켈레톤, 완료되면 실제 데이터 */}
            {initialLoading ? (
                <PortfolioSkeletonBody styles={styles} />
            ) : (
                <PortfolioTableBody
                    items={items}
                    hasMore={hasMore}
                    isFetchingMore={isFetchingMore}
                    observerTargetRef={observerTargetRef}
                    navigate={navigate}
                    t={t}
                    getReturnValue={getReturnValue}
                    getDdValue={getDdValue}
                    getSharpeValue={getSharpeValue}
                    getCalmarValue={getCalmarValue}
                    getSortinoValue={getSortinoValue}
                    getVolValue={getVolValue}
                    handleLockedMetricClick={handleLockedMetricClick}
                    styles={styles} // 스타일 모듈 전달
                />
            )}
          </table>
        </div>
      )}
    </div>
  );
}